/**
 * Chips de contraoferta del conductor (ExtraInfo / DriverTrips).
 *
 * - Si el pasajero ofreció el mínimo → [mínimo, precio sistema, sistema×1.1]
 * - Si envió otra oferta (> mínimo) → chips centrados en esa oferta
 *   [max(piso, oferta×0.9), oferta, oferta×1.1] para coherencia con lo enviado
 *   (ida/vuelta, montos custom, etc.)
 * - El conductor nunca puede ofertar por debajo de customer_min_offer.
 */

const round2 = (n) => parseFloat((Number(n) || 0).toFixed(2));

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Precio real / recomendado del sistema (ya incluye ×2 si ida y vuelta). */
export function driverRealPrice(item = {}) {
  return num(item.system_estimate) || num(item.estimate) || 0;
}

/** Oferta enviada por el pasajero. */
export function driverCustomerOffer(item = {}) {
  const offer = num(item.customer_offer);
  if (offer > 0) return offer;
  // Legacy bidcab: `estimate` era la oferta cuando difiere del system_estimate.
  const sys = num(item.system_estimate);
  const est = num(item.estimate);
  if (est > 0 && sys > 0 && Math.abs(est - sys) > Math.max(1, sys * 0.005)) return est;
  return 0;
}

/** Piso absoluto: mínimo que el pasajero podía ofertar al crear el viaje. */
export function driverOfferFloor(item = {}) {
  const stored = num(item.customer_min_offer);
  if (stored > 0) return stored;
  const real = driverRealPrice(item);
  const offer = driverCustomerOffer(item);
  // Legacy sin customer_min_offer: si la oferta quedó claramente bajo el real, esa es el piso.
  if (offer > 0 && real > 0 && offer < real * 0.98) return offer;
  if (real > 0) return round2(real * 0.9);
  return offer || 0;
}

/** true si la oferta del cliente está en (o muy cerca de) el mínimo permitido. */
export function customerOfferedMinimum(item = {}) {
  const floor = driverOfferFloor(item);
  const offer = driverCustomerOffer(item);
  if (!(floor > 0) || !(offer > 0)) return false;
  const tol = Math.max(1, floor * 0.005);
  return offer <= floor + tol;
}

/**
 * @returns {{ chips: number[], bidFloor: number, realPrice: number, offeredAtMinimum: boolean }}
 */
export function resolveDriverBidChips(item = {}) {
  const real = driverRealPrice(item);
  const offer = driverCustomerOffer(item);
  const floor = driverOfferFloor(item);
  const offeredAtMinimum = customerOfferedMinimum(item);

  let chipLow;
  let chipMid;
  let chipHigh;

  if (offeredAtMinimum) {
    // Oferta al piso: chips invitan a subir hacia el precio del sistema.
    chipLow = round2(floor);
    chipMid = round2(real > 0 ? real : floor);
    chipHigh = round2(chipMid * 1.1);
  } else if (offer > 0) {
    // Centrar en la oferta enviada (coherente con lo que ve el conductor en "Oferta").
    chipMid = round2(offer);
    chipLow = round2(chipMid * 0.9);
    chipHigh = round2(chipMid * 1.1);
    if (floor > 0 && chipLow < floor) chipLow = round2(floor);
  } else {
    chipMid = round2(real > 0 ? real : floor);
    chipLow = round2(chipMid * 0.9);
    chipHigh = round2(chipMid * 1.1);
    if (floor > 0 && chipLow < floor) chipLow = round2(floor);
  }

  if (chipMid < chipLow) chipMid = chipLow;
  if (chipHigh < chipMid) chipHigh = round2(chipMid * 1.1);

  const bidFloor = round2(floor > 0 ? floor : chipLow);

  return {
    chips: [chipLow, chipMid, chipHigh],
    bidFloor,
    realPrice: round2(real > 0 ? real : chipMid),
    offeredAtMinimum,
  };
}

export function driverMayBidPrice(item, price) {
  const p = parseFloat(price);
  if (!(p > 0)) return { ok: false, reason: 'invalid', bidFloor: 0 };
  const { bidFloor } = resolveDriverBidChips(item);
  if (bidFloor > 0 && p < bidFloor - 0.001) {
    return { ok: false, reason: 'below_min', bidFloor };
  }
  return { ok: true, bidFloor };
}
